import React, { useEffect, useState } from 'react';

import {
	Chart,
	ChartControlPanel,
	ExportActionsTypes,
	FileTypeSelectionCommonForm,
	Modal,
	SpinnerOverlay,
	ThresholdText } from '@components/common';

import { kpi } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { prepareTieInChartToExport } from '@utils/kpi-utils/prepareChartData';
import { prepareCorrelateSets } from '@utils/kpi-utils/prepareCorrelateSets';

import { IExportKpiCorrelateDTO } from '@interfaces/kpi/dto/kpiDTO';
import { ICorrelateChartsCardTypes } from './CorrelateChartsCard.types';

import { TExportFileTypes } from '@enums/common';

import { exportRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';

const CorrelateChartsCard = ({
	correlateCharts,
	exportCorrelateCharts,
	getCorrelation,
	isLoadingCorrelation,
}: ICorrelateChartsCardTypes) => {
	const { chartsReceivedAt } = useAppSelector(kpi);

	const [isThreshold, setIsThreshold] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const toggleThreshold = () => setIsThreshold(prevState => !prevState);
	const isDisabledThreshold = correlateCharts.every(chart => !chart.thresholds.length);

	useEffect(() => {
		if (!correlateCharts.length) return;

		const sets = prepareCorrelateSets(correlateCharts);

		getCorrelation({ set1: sets[0], set2: sets[1] });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [correlateCharts]);

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const closeTypeSelectionForm = () => setIsOpenFileTypeSelectionModal(false);

	const exportHandler = (data: { fileType: TExportFileTypes }) => {
		if (!chartsReceivedAt) return;

		const sets = prepareCorrelateSets(correlateCharts);

		const exportData: Array<IExportKpiCorrelateDTO> = correlateCharts.map(chart => ({
			...prepareTieInChartToExport(chart),
			fileType: data.fileType,
			lastSeenTime: chartsReceivedAt,
			correlationInDto: {
				set1: sets[0],
				set2: sets[1],
			},
		}));

		exportCorrelateCharts(exportData);
	};

	return (
		<div>
			{isLoadingCorrelation && <SpinnerOverlay />}
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={exportHandler}
					isLoading={false}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			<Chart
				charts={[correlateCharts[0]]}
				isThreshold={isThreshold}
			/>
			<ThresholdText
				chart={correlateCharts[0]}
				isVisible={isThreshold}
			/>
			<Chart
				charts={[correlateCharts[1]]}
				colorIndex={1}
				isThreshold={isThreshold}
			/>
			<ThresholdText
				chart={correlateCharts[1]}
				isVisible={isThreshold}
			/>
			<ChartControlPanel
				chart={correlateCharts[0]}
				charts={correlateCharts}
				downloadChart={toggleFileTypeSelectionModal}
				isCheckedThreshold={isThreshold}
				isDisabledThreshold={isDisabledThreshold}
				isFullCard
				toggleThreshold={toggleThreshold}
			/>
		</div>
	);
};

export default CorrelateChartsCard;
